import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import {
  getNotifications,
  getUnreadCount,
  mapServerNotification,
  markAllAsRead,
  markAsRead,
  removeNotification,
} from "@/services";
import { Notification, ServerNotification } from "@/types";
import { handleError } from "@/utils";

import { useSocket } from "./useSocket";

const NOTIFICATIONS_QUERY_KEY = ["notifications"];
const NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY = ["notifications-unread-count"];

type QueryClient = ReturnType<typeof useQueryClient>;

const getCachedNotification = (queryClient: QueryClient, id: string) =>
  queryClient.getQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY)?.find((n) => n._id === id);

const decrementUnreadIfWasUnread = (queryClient: QueryClient, target: Notification | undefined) => {
  if (target && !target.isRead) {
    queryClient.setQueryData<number>(NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY, (c = 0) =>
      Math.max(0, c - 1),
    );
  }
};

export function useNotifications() {
  const socket = useSocket();
  const queryClient = useQueryClient();

  const {
    data: notifications = [],
    isLoading: notificationsLoading,
    isError: notificationsFailed,
    isFetching: notificationsFetching,
    refetch: refetchNotifications,
  } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: getNotifications,
    refetchOnMount: "always",
  });

  const { data: unreadCount = 0, isLoading: countLoading } = useQuery({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
    queryFn: getUnreadCount,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = (row: ServerNotification) => {
      const mapped = mapServerNotification(row);
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (prev = []) =>
        prev.some((n) => n._id === mapped._id) ? prev : [mapped, ...prev],
      );
    };

    const handleCountNotification = (count: number) => {
      queryClient.setQueryData<number>(NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY, count);
    };

    socket.on("newNotification", handleNewNotification);
    socket.on("countNotification", handleCountNotification);

    return () => {
      socket.off("newNotification", handleNewNotification);
      socket.off("countNotification", handleCountNotification);
    };
  }, [socket, queryClient]);

  const markOneAsRead = async (id: string) => {
    try {
      await markAsRead(id);
      const target = getCachedNotification(queryClient, id);
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (prev = []) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );
      decrementUnreadIfWasUnread(queryClient, target);
    } catch (error) {
      handleError(error);
    }
  };

  const markAllRead = async () => {
    try {
      await markAllAsRead();
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (prev = []) =>
        prev.map((n) => ({ ...n, isRead: true })),
      );
      queryClient.setQueryData<number>(NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY, 0);
    } catch (error) {
      handleError(error);
    }
  };

  const deleteOne = async (id: string) => {
    try {
      await removeNotification(id);
      const target = getCachedNotification(queryClient, id);
      queryClient.setQueryData<Notification[]>(NOTIFICATIONS_QUERY_KEY, (prev = []) =>
        prev.filter((n) => n._id !== id),
      );
      decrementUnreadIfWasUnread(queryClient, target);
    } catch (error) {
      handleError(error);
    }
  };

  return {
    notifications,
    unreadCount,
    isLoading: notificationsLoading || countLoading,
    notificationsFailed,
    notificationsFetching,
    refetchNotifications,
    markOneAsRead,
    markAllRead,
    deleteOne,
  };
}
